import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import Card from 'react-bootstrap/Card';
import Modal from 'react-bootstrap/Modal';
import api from '../../utils/api';
import AdminHeader from '../../components/AdminHeader';
import { getStatusConfig } from '../../utils/orderStatusConstants';
import './AdminBillings.css';

export default function AdminBillings({ admin, logout, isSidebarCollapsed, toggleSidebar }) {
  const navigate = useNavigate();
  const [billings, setBillings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [daysFilter, setDaysFilter] = useState('5');
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(false);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [showSlipModal, setShowSlipModal] = useState(false);
  const [selectedSlipUrl, setSelectedSlipUrl] = useState('');
  const [rejectingPaymentId, setRejectingPaymentId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [ordersWithFirstInstallmentPaid, setOrdersWithFirstInstallmentPaid] = useState(new Set());

  useEffect(() => {
    if (!admin) {
      navigate('/admin/login');
    } else {
      fetchBillings();
      fetchPendingPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, navigate, statusFilter, daysFilter]);

  const fetchBillings = async () => {
    try {
      setIsLoading(true);
      let url = '/api/admin/billings/upcoming';
      
      // ถ้าเลือก filter อื่นๆ ใช้ endpoint ที่รองรับ query params
      if (statusFilter !== 'pending' || daysFilter !== '5') {
        url = `/api/admin/billings?status=${statusFilter}&days=${daysFilter}`;
      }
      
      const token = localStorage.getItem('admin_token');
      const response = await api.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const billingsData = response.data || [];
      setBillings(billingsData);
      
      // ตรวจสอบว่า order ไหนมี installment งวดแรกชำระแล้ว
      const paidFirstInstallmentOrders = new Set();
      const uniqueOrderIds = [...new Set(billingsData.map(b => b.order_id))];
      
      // Fetch order details เพื่อตรวจสอบ installment งวดแรก
      await Promise.all(
        uniqueOrderIds.map(async (orderId) => {
          try {
            const orderResponse = await api.get(`/api/orders/${orderId}`);
            const order = orderResponse.data;
            
            // ตรวจสอบว่าเป็น installment order และงวดแรกชำระแล้วหรือไม่
            const isInstallment = order.installment_periods > 1 || 
                                 (order.payment_method && order.payment_method.toLowerCase().includes('install'));
            
            if (isInstallment && order.installments && order.installments.length > 0) {
              const firstInstallment = order.installments.find(inst => inst.installment_number === 1);
              if (firstInstallment && firstInstallment.payment_status === 'paid') {
                paidFirstInstallmentOrders.add(orderId);
              }
            }
          } catch (error) {
            console.error(`Error fetching order ${orderId}:`, error);
          }
        })
      );
      
      setOrdersWithFirstInstallmentPaid(paidFirstInstallmentOrders);
      setLastFetchedAt(new Date());
    } catch (error) {
      console.error('Error fetching billings:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
        navigate('/admin/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPendingPayments = async () => {
    try {
      setIsLoadingPayments(true);
      const token = localStorage.getItem('admin_token');
      const response = await api.get('/api/admin/payments?status=pending', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setPendingPayments(response.data || []);
    } catch (error) {
      console.error('Error fetching pending payments:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
        navigate('/admin/login');
      }
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const handleApprovePayment = async (paymentId) => {
    if (!window.confirm('ยืนยันการอนุมัติการชำระเงินนี้หรือไม่?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await api.patch(`/api/admin/payments/${paymentId}/approve`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert('อนุมัติการชำระเงินสำเร็จ');
      fetchPendingPayments();
      fetchBillings(); // Refresh billings as well (จะอัปเดต ordersWithFirstInstallmentPaid ด้วย)
    } catch (error) {
      console.error('Error approving payment:', error);
      alert(error.response?.data?.error || 'ไม่สามารถอนุมัติการชำระเงินได้');
    }
  };

  const handleRejectPayment = async (paymentId) => {
    if (!rejectionReason.trim()) {
      alert('กรุณาระบุเหตุผลในการปฏิเสธ');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await api.patch(`/api/admin/payments/${paymentId}/reject`, {
        rejection_reason: rejectionReason
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert('ปฏิเสธการชำระเงินสำเร็จ');
      setRejectingPaymentId(null);
      setRejectionReason('');
      fetchPendingPayments();
    } catch (error) {
      console.error('Error rejecting payment:', error);
      alert(error.response?.data?.error || 'ไม่สามารถปฏิเสธการชำระเงินได้');
    }
  };

  const formatCurrency = (value) => {
    if (value == null || value === '') {
      return '-';
    }
    const number = Number(value);
    if (Number.isNaN(number)) {
      return '-';
    }
    return `฿${number.toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (value) => {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear() + 543; // Convert to Buddhist Era
    return `${day}/${month}/${year}`;
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleViewOrderDetails = async (orderId) => {
    try {
      setIsLoadingOrderDetails(true);
      setShowOrderModal(true);
      const response = await api.get(`/api/orders/${orderId}`);
      setSelectedOrder(response.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      alert('ไม่สามารถโหลดรายละเอียดออเดอร์ได้');
      setShowOrderModal(false);
    } finally {
      setIsLoadingOrderDetails(false);
    }
  };

  const handleCloseOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };


  const getPaymentMethodLabel = (order) => {
    if (!order) return '-';
    const method = (order.payment_method || '').toString().toLowerCase();
    if (method.includes('install')) return 'ผ่อนชำระ';
    if (method.includes('cod') || method.includes('delivery') || method.includes('ปลายทาง')) return 'เก็บเงินปลายทาง';
    return 'ชำระเต็มจำนวน';
  };

  const isInstallmentOrder = (order) => {
    if (!order) return false;
    const method = (order.payment_method || '').toString().toLowerCase();
    return method.includes('install') || Number(order.installment_periods) > 1;
  };

  const getOrderStatusBadge = (status) => {
    const config = getStatusConfig(status);
    return (
      <Badge bg={config.variant} style={{ backgroundColor: config.color, color: config.textColor }}>
        {config.label}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', text: 'รอชำระ', color: '#FFD700', textColor: '#222' },
      paid: { variant: 'success', text: 'ชำระแล้ว', color: '#1DB954', textColor: '#fff' },
      overdue: { variant: 'danger', text: 'เลยกำหนด', color: '#FF6B6B', textColor: '#fff' }
    };
    const config = statusConfig[status] || { variant: 'secondary', text: status, color: '#666', textColor: '#fff' };
    return (
      <Badge bg={config.variant} style={{ backgroundColor: config.color, color: config.textColor }}>
        {config.text}
      </Badge>
    );
  };

  const filteredBillings = useMemo(() => {
    let filtered = billings;

    // Filter: แสดงเฉพาะ billing ที่ order นั้นมี installment งวดแรกชำระแล้ว
    filtered = filtered.filter((billing) => {
      // ตรวจสอบว่า order นี้มี installment งวดแรกชำระแล้วหรือไม่
      return ordersWithFirstInstallmentPaid.has(billing.order_id);
    });

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((billing) => {
        const customerName = `${billing.customer?.fname || ''} ${billing.customer?.lname || ''}`.toLowerCase();
        const orderId = String(billing.order_id || '');
        const mobile = billing.mobile || billing.customer?.tel || '';
        return (
          customerName.includes(searchLower) ||
          orderId.includes(searchLower) ||
          mobile.includes(searchLower)
        );
      });
    }

    return filtered;
  }, [billings, searchTerm, ordersWithFirstInstallmentPaid]);

  const totalAmount = useMemo(() => {
    return filteredBillings.reduce((sum, billing) => sum + (Number(billing.amount) || 0), 0);
  }, [filteredBillings]);

  if (isLoading) {
    return (
      <div className="admin-billings">
        <AdminHeader
          admin={admin}
          onLogout={logout}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
        <div className="admin-billings__loading">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3 text-muted">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-billings">
      <AdminHeader
        admin={admin}
        onLogout={logout}
        isCollapsed={isSidebarCollapsed}
        onToggleSidebar={toggleSidebar}
      />

      <Container fluid className="admin-billings__container">
        {/* Header Section */}
        <section className="admin-billings__header">
          <div>
            <h1 className="admin-billings__title">การเรียกเก็บเงิน (Billing)</h1>
            <p className="admin-billings__subtitle">รายการเรียกเก็บเงินสำหรับออเดอร์ผ่อนชำระที่กำลังจะถึงกำหนดชำระ</p>
          </div>
          <div className="admin-billings__header-meta">
            {lastFetchedAt && (
              <span className="admin-billings__last-updated">
                <i className="bi bi-clock"></i>
                อัปเดตล่าสุด: {lastFetchedAt.toLocaleTimeString('th-TH')}
              </span>
            )}
            <Button variant="primary" onClick={fetchBillings}>
              <i className="bi bi-arrow-repeat me-1"></i>
              รีเฟรชข้อมูล
            </Button>
          </div>
        </section>

        {/* Filters Section */}
        <Card className="admin-billings__filters">
          <div className="admin-billings__filters-content">
            <Form.Group className="admin-billings__search">
              <Form.Control
                type="text"
                placeholder="ค้นหา: ชื่อลูกค้า, Order ID, เบอร์โทร"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <i className="bi bi-search admin-billings__search-icon"></i>
            </Form.Group>

            <div className="admin-billings__filter-group">
              <Form.Label>สถานะ:</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="pending">รอชำระ</option>
                <option value="paid">ชำระแล้ว</option>
                <option value="overdue">เลยกำหนด</option>
                <option value="">ทั้งหมด</option>
              </Form.Select>
            </div>

            <div className="admin-billings__filter-group">
              <Form.Label>ภายใน (วัน):</Form.Label>
              <Form.Select
                value={daysFilter}
                onChange={(e) => setDaysFilter(e.target.value)}
              >
                <option value="3">3 วัน</option>
                <option value="5">5 วัน</option>
                <option value="7">7 วัน</option>
                <option value="14">14 วัน</option>
                <option value="30">30 วัน</option>
              </Form.Select>
            </div>
          </div>
        </Card>

        {/* Summary Card */}
        <Card className="admin-billings__summary">
          <div className="admin-billings__summary-content">
            <div>
              <span className="admin-billings__summary-label">จำนวนรายการ:</span>
              <strong className="admin-billings__summary-value">{filteredBillings.length} รายการ</strong>
            </div>
            <div>
              <span className="admin-billings__summary-label">ยอดรวม:</span>
              <strong className="admin-billings__summary-value">{formatCurrency(totalAmount)}</strong>
            </div>
          </div>
        </Card>

        {/* Billings Table */}
        <Card className="admin-billings__table-card">
          <div className="admin-billings__table-header">
            <h2>รายการเรียกเก็บเงิน (ออเดอร์ผ่อนชำระ)</h2>
          </div>
          <div className="admin-billings__table-body">
            {filteredBillings.length > 0 ? (
              <Table hover className="admin-billings__table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Order ID</th>
                    <th>ลูกค้า</th>
                    <th>เบอร์โทร</th>
                    <th>ยอดชำระ</th>
                    <th>กำหนดชำระ</th>
                    <th>เหลืออีก</th>
                    <th>สถานะ</th>
                    <th className="text-center">จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBillings.map((billing) => {
                    const daysUntil = getDaysUntilDue(billing.due_date);
                    return (
                      <tr key={billing.billing_id}>
                        <td className="fw-semibold">#{billing.billing_id}</td>
                        <td>
                          <Button
                            variant="link"
                            className="p-0"
                            onClick={() => navigate(`/admin/orders?search=${billing.order_id}`)}
                          >
                            #{billing.order_id}
                          </Button>
                        </td>
                        <td>
                          <div>
                            <strong>
                              {billing.customer?.fname || ''} {billing.customer?.lname || ''}
                            </strong>
                            {billing.installment_number && (
                              <div className="text-muted small">
                                งวดที่ {billing.installment_number}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>{billing.mobile || billing.customer?.tel || '-'}</td>
                        <td className="fw-semibold">{formatCurrency(billing.amount)}</td>
                        <td>{formatDate(billing.due_date)}</td>
                        <td>
                          {daysUntil !== null && (
                            <Badge
                              bg={daysUntil < 0 ? 'danger' : daysUntil === 0 ? 'danger' : daysUntil <= 2 ? 'warning' : 'info'}
                              className={`admin-billings-days-badge ${daysUntil === 0 ? 'admin-billings-today-badge' : ''}`}
                            >
                              {daysUntil < 0 ? 'เลยกำหนด' : daysUntil === 0 ? 'วันนี้' : `อีก ${daysUntil} วัน`}
                            </Badge>
                          )}
                        </td>
                        <td>{getStatusBadge(billing.payment_status)}</td>
                        <td className="text-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewOrderDetails(billing.order_id)}
                          >
                            <i className="bi bi-eye"></i>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <div className="admin-billings__empty-state">
                <i className="bi bi-inbox"></i>
                <strong>ไม่พบข้อมูล</strong>
                <p>ไม่มีรายการเรียกเก็บเงินที่ตรงกับเงื่อนไขที่เลือก</p>
              </div>
            )}
          </div>
        </Card>

        {/* Pending Payments Section - สลิปที่ลูกค้าส่งมา */}
        <Card className="admin-billings__table-card mt-4">
          <div className="admin-billings__table-header">
            <h2>รายการแจ้งชำระเงินที่รออนุมัติ (สลิปที่ลูกค้าส่งมา)</h2>
            <p className="text-muted mb-0" style={{ fontSize: '0.9rem', fontWeight: 'normal', marginTop: '0.5rem' }}>
              <i className="bi bi-info-circle me-1"></i>
              คลิกปุ่ม "ดูสลิป" ในคอลัมน์ "สลิป" เพื่อดูรูปสลิปโอนเงินที่ลูกค้าอัปโหลดมา
            </p>
          </div>
          <div className="admin-billings__table-body">
            {isLoadingPayments ? (
              <div className="text-center p-4">
                <Spinner animation="border" />
              </div>
            ) : pendingPayments.length > 0 ? (
              <Table hover className="admin-billings__table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>ลูกค้า</th>
                    <th>อีเมล</th>
                    <th>เบอร์โทร</th>
                    <th>งวดที่</th>
                    <th>ยอดชำระ</th>
                    <th>สลิป</th>
                    <th>หมายเหตุ</th>
                    <th>วันที่แจ้ง</th>
                    <th className="text-center">จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingPayments.map((payment) => (
                    <tr key={payment.payment_id}>
                      <td>
                        <Button
                          variant="link"
                          className="p-0"
                          onClick={() => navigate(`/admin/orders?search=${payment.order_id}`)}
                        >
                          #{payment.order_id}
                        </Button>
                      </td>
                      <td>{payment.customer_name}</td>
                      <td>{payment.customer_email}</td>
                      <td>{payment.customer_tel || '-'}</td>
                      <td>
                        {payment.installment_number ? (
                          <Badge bg="info">งวดที่ {payment.installment_number}</Badge>
                        ) : (
                          <Badge bg="secondary">ชำระเต็มจำนวน</Badge>
                        )}
                      </td>
                      <td className="fw-bold">{formatCurrency(payment.amount)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {payment.slip_url ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            style={{ display: 'inline-block' }}
                            onClick={() => {
                              setSelectedSlipUrl(payment.slip_url);
                              setShowSlipModal(true);
                            }}
                          >
                            ดูสลิป
                          </Button>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{payment.note || '-'}</td>
                      <td>{formatDate(payment.created_at)}</td>
                      <td>
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleApprovePayment(payment.payment_id)}
                          >
                            <i className="bi bi-check-circle me-1"></i>
                            อนุมัติ
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => setRejectingPaymentId(payment.payment_id)}
                          >
                            <i className="bi bi-x-circle me-1"></i>
                            ปฏิเสธ
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="text-center p-4 text-muted">
                ไม่มีรายการแจ้งชำระเงินที่รออนุมัติ
              </div>
            )}
          </div>
        </Card>

        {/* Slip Image Modal */}
        <Modal show={showSlipModal} onHide={() => setShowSlipModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>สลิปโอนเงิน</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            {selectedSlipUrl && (
              <img
                src={selectedSlipUrl}
                alt="Payment Slip"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSlipModal(false)}>
              ปิด
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Reject Payment Modal */}
        <Modal show={!!rejectingPaymentId} onHide={() => {
          setRejectingPaymentId(null);
          setRejectionReason('');
        }} centered>
          <Modal.Header closeButton>
            <Modal.Title>ปฏิเสธการชำระเงิน</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>เหตุผลในการปฏิเสธ <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="กรุณาระบุเหตุผลในการปฏิเสธการชำระเงิน"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setRejectingPaymentId(null);
              setRejectionReason('');
            }}>
              ยกเลิก
            </Button>
            <Button
              variant="danger"
              onClick={() => handleRejectPayment(rejectingPaymentId)}
              disabled={!rejectionReason.trim()}
            >
              ปฏิเสธ
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>

      {/* Order Details Modal */}
      <Modal
        show={showOrderModal}
        onHide={handleCloseOrderModal}
        size="lg"
        centered
        scrollable
        contentClassName="admin-billings-order-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-receipt me-2"></i>
            รายละเอียดคำสั่งซื้อ {selectedOrder ? `#${selectedOrder.order_id}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingOrderDetails ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">กำลังโหลดข้อมูล...</p>
            </div>
          ) : selectedOrder ? (
            <div>
              <div className="admin-order-detail-section">
                <h4>
                  <i className="bi bi-person"></i>
                  ข้อมูลลูกค้า
                </h4>
                <div className="admin-order-detail-content">
                  <div className="admin-order-detail-row">
                    <span>ชื่อ:</span>
                    <strong>
                      {selectedOrder.customer_fname || ''} {selectedOrder.customer_lname || ''}
                    </strong>
                  </div>
                  <div className="admin-order-detail-row">
                    <span>เบอร์:</span>
                    <strong>{selectedOrder.customer_tel || '-'}</strong>
                  </div>
                  <div className="admin-order-detail-row">
                    <span>อีเมล:</span>
                    <strong>{selectedOrder.customer_email || '-'}</strong>
                  </div>
                </div>
              </div>

              <div className="admin-order-detail-section">
                <h4>
                  <i className="bi bi-box-seam"></i>
                  รายการสินค้า
                </h4>
                <div className="admin-order-detail-content">
                  {selectedOrder.details && selectedOrder.details.length > 0 ? (
                    <div className="admin-order-detail-items">
                      {selectedOrder.details.map((item) => (
                        <div key={item.order_detail_id} className="admin-order-detail-item">
                          <div className="admin-order-detail-item__name">{item.product_name}</div>
                          <div className="admin-order-detail-item__meta">
                            <span>x{item.quantity}</span>
                            <strong>{formatCurrency(item.price)}</strong>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">ไม่มีรายการสินค้า</p>
                  )}
                </div>
              </div>

              <div className="admin-order-detail-section">
                <h4>
                  <i className="bi bi-currency-dollar"></i>
                  สรุปยอด
                </h4>
                <div className="admin-order-detail-content">
                  <div className="admin-order-detail-row">
                    <span>ยอดรวม:</span>
                    <strong className="admin-order-detail-total">
                      {formatCurrency(selectedOrder.total_amount)}
                    </strong>
                  </div>
                  <div className="admin-order-detail-row">
                    <span>วิธีชำระ:</span>
                    <strong>{getPaymentMethodLabel(selectedOrder)}</strong>
                  </div>
                  {isInstallmentOrder(selectedOrder) && (
                    <div className="admin-order-detail-row">
                      <span>จำนวนงวด:</span>
                      <strong>{selectedOrder.installment_periods} งวด</strong>
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-order-detail-section">
                <h4>
                  <i className="bi bi-info-circle"></i>
                  สถานะปัจจุบัน
                </h4>
                <div className="admin-order-detail-content">
                  <div className="admin-order-detail-status">
                    {getOrderStatusBadge(selectedOrder.order_status)}
                  </div>
                </div>
              </div>

              {isInstallmentOrder(selectedOrder) && selectedOrder.installments && (
                <div className="admin-order-detail-section">
                  <h4>
                    <i className="bi bi-calendar-check"></i>
                    ตารางผ่อนชำระ
                  </h4>
                  <div className="admin-order-detail-content">
                    {selectedOrder.installments.length > 0 ? (
                      <div className="admin-order-detail-installments">
                        {selectedOrder.installments.map((installment) => (
                          <div key={installment.installment_id} className="admin-order-detail-installment">
                            <div className="admin-order-detail-installment__number">
                              งวด {installment.installment_number}
                            </div>
                            <div className="admin-order-detail-installment__amount">
                              {formatCurrency(installment.installment_amount)}
                            </div>
                            <div className="admin-order-detail-installment__date">
                              กำหนดชำระ: {formatDate(installment.payment_due_date)}
                            </div>
                            <div className="admin-order-detail-installment__status">
                              <Badge
                                bg={installment.payment_status === 'paid' ? 'success' : 'secondary'}
                              >
                                {installment.payment_status === 'paid' ? 'ชำระแล้ว' : 'รอชำระ'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">ยังไม่มีข้อมูลตารางผ่อนชำระ</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseOrderModal}>
            ปิด
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

